import React, { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { avisar, textoDeError } from './Dialogo';
import { usePermisos } from '../lib/permisos';
import { motivoTxt } from '../lib/motivo';

/**
 * Reglas y datos → Recorrido de la cita (CONTRATO-HISTORIA, 0.57.0). Lo que ve cada persona antes de su cita (textos,
 * videos, botones y para quién) se configura en HALORY; aquí queda lo del HUB: el interruptor de la Historia Safevity, la
 * dirección que se pega en Halory para que pida la liga, y cómo van las citas de los próximos 30 días.
 */
interface Config { historia: { activo: boolean } }
interface Estado {
  proximas: number; pedidas: number; llenadas: number; mensajes: number; porEnviar: number; porLimpiar: number;
  errores: { motivo: string; n: number }[]; ligaErrores: { motivo: string; n: number }[];
}
interface Respuesta {
  config: Config; guardada: boolean; updated_at: string | null; updated_by: string | null; estado: Estado; falta: string[]; draia: string;
  ligaCita: { url: string; firmado: boolean; clave: string; ultimaPeticion: string | null };
}

const plural = (n: number, uno: string, varios: string) => `${n.toLocaleString('es-MX')} ${n === 1 ? uno : varios}`;
const fechaHora = (d: string) => new Date(d).toLocaleString('es-MX', { dateStyle: 'medium', timeStyle: 'short' });
/** "9:36 p.m." ya trae su punto: no se le pone otro. */
const conPunto = (t: string) => (t.endsWith('.') ? t : `${t}.`);
/** "a", "a y b", "a, b y c". */
const enLista = (xs: string[]) => (xs.length > 1 ? `${xs.slice(0, -1).join(', ')} y ${xs[xs.length - 1]}` : xs[0] || '');

export function RecorridoCita() {
  const { esAdmin } = usePermisos();
  const [d, setD] = useState<Respuesta | null>(null);
  const [activo, setActivo] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [msg, setMsg] = useState<{ t: string; ok: boolean } | null>(null);
  const [guardando, setGuardando] = useState(false);
  const [revisando, setRevisando] = useState(false);
  const [copiado, setCopiado] = useState<'' | 'url' | 'clave' | 'no'>('');

  const cargar = () => api<Respuesta>('crm/recorrido').then((r) => { setD(r); setActivo(r.config.historia.activo); }).catch((e) => setErr(textoDeError(e)));
  useEffect(() => { cargar(); }, []);

  if (err) return <div className="card" style={{ padding: 16, color: 'var(--warning)', background: 'var(--warning-bg)' }}>{err}</div>;
  if (!d) return <div className="muted" style={{ padding: 40, textAlign: 'center' }}>Cargando…</div>;

  const cambio = activo !== d.config.historia.activo;
  const copiar = async (t: string, cual: 'url' | 'clave') => {
    try { await navigator.clipboard.writeText(t); setCopiado(cual); setTimeout(() => setCopiado(''), 2000); } catch { setCopiado('no'); }
  };
  const guardar = async () => {
    setGuardando(true); setMsg(null);
    try {
      await api('crm/recorrido', { method: 'PUT', body: JSON.stringify({ historia: { activo } }) });
      setMsg({ t: activo ? 'Guardado. Cuando Halory pida la liga de la historia, Dra. IA la crea para esa cita.'
        : 'Guardado. Cuando Halory pida la liga de la historia, DTB HUB contesta que no hay y Halory esconde ese botón.', ok: true });
      cargar();
    } catch (e) { await avisar({ titulo: 'No se pudo guardar', mensaje: textoDeError(e), tipo: 'error' }); }
    setGuardando(false);
  };
  const revisar = async () => {
    setRevisando(true); setMsg(null);
    try {
      const r = await api<{ tanda: { revisadas: number; enviadas: number; limpiadas: number; canceladas: number; errores: number; ultimoError?: string; motivo?: string } }>('crm/recorrido/revisar', { method: 'POST', body: '{}' });
      const t = r.tanda;
      const partes = [
        t.enviadas ? plural(t.enviadas, 'mensaje de su médico enviado', 'mensajes de su médico enviados') : '',
        t.limpiadas ? plural(t.limpiadas, 'página limpiada', 'páginas limpiadas') : '',
        t.canceladas ? plural(t.canceladas, 'historia cancelada en Dra. IA', 'historias canceladas en Dra. IA') : '',
        t.errores ? `${plural(t.errores, 'no se pudo', 'no se pudieron')} (${motivoTxt(t.ultimoError)})` : '',
      ].filter(Boolean);
      setMsg({ t: t.revisadas ? `Se revisaron ${plural(t.revisadas, 'cita', 'citas')}${partes.length ? `: ${partes.join(' · ')}` : ''}.`
        : `No había nada que cambiar en las citas de los próximos 30 días${t.motivo ? ` (${motivoTxt(t.motivo)})` : ''}.`, ok: !t.errores });
      cargar();
    } catch (e) { setMsg({ t: textoDeError(e), ok: false }); }
    setRevisando(false);
  };

  const e = d.estado;
  const fila = { padding: '12px 14px', fontSize: 13, lineHeight: 1.55 } as const;
  const codigo: React.CSSProperties = { fontSize: 12, background: 'var(--bg-alt)', padding: '4px 8px', borderRadius: 8, overflowWrap: 'anywhere', minWidth: 0 };
  return (
    <div data-recorrido style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: 10 }}>
      <div className="card" style={{ ...fila, fontSize: 13.5 }} data-recorrido-halory>
        <strong>Lo que ve cada persona antes de su cita</strong> (textos, videos, botones y para quién) se configura en
        <strong> Halory → Configuración → Recorrido de la cita → Lo que ve antes de su cita</strong>. Ahí se arman varias
        configuraciones y se decide cuál se usa en cada caso.
        <div style={{ marginTop: 6 }}>
          La liga de la <strong>Historia Safevity</strong> la pide Halory a DTB HUB: en la regla, el botón «La da tu sistema» con
          la clave «historia». Lo único que DTB HUB pone por su cuenta en la página de una cita es el mensaje que aprobó su
          médico en Dra. IA: <strong>nada le llega sin que un médico lo apruebe</strong>, y las alertas de la historia se quedan para el equipo.
        </div>
      </div>
      {d.falta.length > 0 && <div className="card" style={{ ...fila, color: 'var(--warning)', background: 'var(--warning-bg)', border: 'none' }} data-recorrido-falta>
        Falta {conPunto(enLista(d.falta))}
      </div>}
      {msg && <div role="status" className="card" data-recorrido-msg={msg.ok ? 'ok' : 'error'} style={{ ...fila, border: 'none', background: msg.ok ? 'var(--success-bg)' : 'var(--warning-bg)', color: msg.ok ? 'var(--success)' : 'var(--warning)' }}>{msg.t}</div>}

      <div className="card" style={fila} data-recorrido-liga>
        <div style={{ fontWeight: 600, marginBottom: 6 }}>La dirección que se pega en Halory</div>
        <div className="muted" style={{ fontSize: 12.5, marginBottom: 6 }}>En Halory → Configuración → Recorrido de la cita, donde pide la dirección de tu sistema para las ligas:</div>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
          <code style={codigo} data-liga-url>{d.ligaCita.url}</code>
          <button className="btn-ghost" style={{ padding: '3px 10px', fontSize: 12.5 }} onClick={() => copiar(d.ligaCita.url, 'url')} data-copiar-liga>{copiado === 'url' ? 'Copiada' : 'Copiar'}</button>
        </div>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', marginTop: 8 }}>
          <span className="muted" style={{ fontSize: 12.5 }}>Clave del botón:</span>
          <code style={codigo} data-liga-clave>{d.ligaCita.clave}</code>
          <button className="btn-ghost" style={{ padding: '3px 10px', fontSize: 12.5 }} onClick={() => copiar(d.ligaCita.clave, 'clave')} data-copiar-clave>{copiado === 'clave' ? 'Copiada' : 'Copiar'}</button>
        </div>
        {copiado === 'no' && <div style={{ marginTop: 4, color: 'var(--warning)', fontSize: 12.5 }}>El navegador no dejó copiar: selecciona el texto y cópialo a mano.</div>}
        <div style={{ marginTop: 8, fontSize: 12.5 }} data-liga-ultima>
          {d.ligaCita.ultimaPeticion
            ? <>La última vez que Halory pidió una liga: {conPunto(fechaHora(d.ligaCita.ultimaPeticion))}</>
            : <span className="muted">Halory todavía no ha pedido ninguna liga: aparece aquí la primera vez que alguien abra la página de su cita con ese botón.</span>}
        </div>
        {!d.ligaCita.firmado && <div style={{ marginTop: 4, color: 'var(--warning)', fontSize: 12.5 }} data-liga-sin-secreto>
          Falta <code>HALORY_WEBHOOK_SECRET</code> en DTB HUB: sin él la dirección contesta que no está configurada y Halory no enseña el botón.
        </div>}
      </div>

      <div className="card" style={fila} data-recorrido-historia>
        <div style={{ fontWeight: 600, marginBottom: 6 }}>Historia Safevity</div>
        <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
          <input type="checkbox" checked={activo} disabled={!esAdmin} data-historia-activo onChange={(ev) => setActivo(ev.target.checked)} style={{ marginTop: 3 }} />
          <span>Dra. IA puede crear la historia cuando Halory la pida</span>
        </label>
        <div className="muted" style={{ fontSize: 12.5, marginTop: 6, paddingLeft: 26 }}>
          {activo ? 'Cada cita tiene su propia historia: pedirla otra vez da la misma liga. Se llena antes de la consulta y, cuando la cita se cancela, se cancela sola.'
            : 'Apagado: cuando Halory pida la liga, DTB HUB contesta que no hay y Halory esconde ese botón. Lo demás de la página de la cita no cambia.'}
        </div>
        <div className="muted" style={{ fontSize: 12, marginTop: 8 }}>
          La revisan en Dra. IA ({d.draia.replace(/^https?:\/\//, '')}): el equipo médico edita el mensaje y lo aprueba; en ese momento llega a la página de la cita.
        </div>
      </div>

      <div className="card" style={fila} data-recorrido-estado>
        <div style={{ display: 'flex', gap: 10, alignItems: 'baseline', justifyContent: 'space-between', flexWrap: 'wrap' }}>
          <div style={{ fontWeight: 600 }}>Las citas de los próximos 30 días</div>
          {esAdmin && <button className="btn-ghost" style={{ padding: '4px 12px', fontSize: 13 }} disabled={revisando} onClick={revisar} data-recorrido-revisar>{revisando ? 'Revisando…' : 'Revisar ahora'}</button>}
        </div>
        {e.proximas === 0
          ? <span className="muted">No hay citas de Halory en los próximos 30 días.</span>
          : <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
              <li>{plural(e.proximas, 'cita de Halory', 'citas de Halory')} en total.</li>
              <li data-estado-pedidas>Halory ya pidió la liga de la historia en {plural(e.pedidas, 'cita', 'citas')}{e.llenadas ? ` (${plural(e.llenadas, 'ya la llenó', 'ya la llenaron')})` : ''}.</li>
              <li data-estado-mensajes>El mensaje de su médico ya está en la página de {plural(e.mensajes, 'cita', 'citas')}.</li>
              {e.porEnviar > 0 && <li data-estado-por-enviar>{plural(e.porEnviar, 'mensaje aprobado que todavía no llega', 'mensajes aprobados que todavía no llegan')}: se mandan solos en las siguientes vueltas del reloj.</li>}
              {e.porLimpiar > 0 && <li className="muted" data-estado-por-limpiar>{plural(e.porLimpiar, 'página con lo de la versión anterior', 'páginas con lo de la versión anterior')} (el video y los textos fijos): se quitan solos en el reloj.</li>}
              {e.errores.map((x) => <li key={'e' + x.motivo} style={{ color: 'var(--warning)' }}>{plural(x.n, 'página no se pudo actualizar', 'páginas no se pudieron actualizar')}: {motivoTxt(x.motivo)}. Se vuelve a intentar solo.</li>)}
              {e.ligaErrores.map((x) => <li key={'l' + x.motivo} style={{ color: 'var(--warning)' }} data-estado-liga-error>{plural(x.n, 'liga que Halory pidió y no se pudo dar', 'ligas que Halory pidió y no se pudieron dar')}: {motivoTxt(x.motivo)}.</li>)}
            </ul>}
      </div>

      {esAdmin
        ? <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <button className="btn" disabled={guardando || !cambio} onClick={guardar} data-recorrido-guardar>{guardando ? 'Guardando…' : 'Guardar'}</button>
            {d.updated_at && <span className="muted" style={{ fontSize: 12 }}>Última vez: {fechaHora(d.updated_at)}{d.updated_by ? ` · ${d.updated_by}` : ''}</span>}
          </div>
        : <div className="muted" style={{ fontSize: 12.5 }}>Sólo una cuenta de admin cambia el interruptor.</div>}
    </div>
  );
}
